import cx from 'classnames'
import { AI } from 'blaze-ui/icons'
import Styles from './PromptInput.module.scss'
import { PromptInputProps } from './Types'
import { forwardRef } from 'react'
import { useObjectRef } from '@react-aria/utils'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Send2 } from 'blaze-ui/icons/16'
import {
  FloatingPortal,
  autoUpdate,
  flip,
  hide,
  inline,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react-dom-interactions'
import { useOverlayTriggerState } from '@react-stately/overlays'
import { Popover } from 'components/Popover/Popover'
import { QuickActionsMenu } from './QuickActionsMenu'

const PromptInput = forwardRef<HTMLTextAreaElement, PromptInputProps>(
  ({ view, attrs, value, onChange, onSubmit, isFocused = false }, fRef) => {
    const isActive = isFocused || value !== ''

    const { isOpen, close } = useOverlayTriggerState({ isOpen: isFocused && value === '' })
    const { x, y, reference, floating, middlewareData } = useFloating({
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [offset(8), inline(), flip(), shift(), hide()],
      whileElementsMounted: (reference, floating, update) => {
        return autoUpdate(reference, floating, update, {
          animationFrame: true,
        })
      },
    })

    const textareaRef = useObjectRef<HTMLTextAreaElement>(fRef)

    const onResize = () => {
      if (textareaRef.current) {
        textareaRef.current.style.height = ''
        const height = Math.min(100, textareaRef.current.scrollHeight)
        textareaRef.current.style.height = `${height}px`
      }
    }

    const handleSubmit = () => {
      onSubmit(value)
    }

    return (
      <>
        <div className={Styles.root}>
          <div className={cx(Styles.wrapper, { [Styles.focused]: isActive })} ref={reference}>
            <AI />
            <textarea
              ref={textareaRef}
              className={Styles.textarea}
              name="prompt"
              placeholder="Describe the image you want..."
              value={value}
              onChange={(e) => {
                onChange(e.target.value)
                onResize()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleSubmit()
                }

                if (e.key === 'Escape') {
                  onChange('')
                  e.currentTarget.blur()
                }
              }}
            />
          </div>
          {isActive && (
            <div className={Styles.sendButton}>
              <IconButton
                size="sm"
                icon={Send2}
                onPress={handleSubmit}
                isDisabled={!value}
                aria-label="Search images"
              />
            </div>
          )}
        </div>
        {isOpen && attrs.source === 'unsplash' && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={{
                position: 'fixed',
                visibility: !middlewareData.hide?.referenceHidden && x !== null && y !== null ? 'visible' : 'hidden',
                top: y || 0,
                left: x || 0,
              }}
              isOpen={isOpen}
              onClose={close}
              ref={floating}
              containFocus
              restoreFocus
              autoFocus={false}
              className={Styles.menu}
              noOverlay
            >
              <QuickActionsMenu view={view} attrs={attrs} />
            </Popover>
          </FloatingPortal>
        )}
      </>
    )
  },
)

export { PromptInput }
